/**
 * VT Wishlist — standalone ES module for Shopify Horizon 4.0 (OS 2.0).
 *
 * Provides:
 *   - VTWishlist: localStorage-backed wishlist API (add / remove / has / getAll / getCount)
 *   - <vt-wishlist-button>: toggles a product in the wishlist; reflects active state.
 *   - <vt-wishlist-page>: reads wishlist, fetches product data via /products/{handle}.js,
 *     renders product cards in a grid with remove + add-to-cart + add-all-to-cart.
 *
 * Storage key: "vt_wishlist"  ->  JSON array of product handles (strings).
 *
 * No jQuery. Vanilla JS only. No theme imports — fully standalone.
 *
 * @module vt-wishlist
 */

/* ========================================================================== *
 * VTWishlist — localStorage API
 * ========================================================================== */

const STORAGE_KEY = 'vt_wishlist';

/**
 * Reads the wishlist array from localStorage.
 * @returns {string[]} Array of product handles (de-duplicated, order preserved).
 */
function readStore() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter((h) => typeof h === 'string') : [];
  } catch (err) {
    console.warn('[VTWishlist] Could not read wishlist from localStorage:', err);
    return [];
  }
}

/**
 * Writes the wishlist array to localStorage and dispatches a `wishlist:updated`
 * CustomEvent on `document` (bubbles, cancelable). Detail: `{ count, handle }`.
 * @param {string[]} list - The new wishlist array.
 * @param {string} [handle] - The handle that triggered the change (optional).
 */
function writeStore(list, handle) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
  } catch (err) {
    console.warn('[VTWishlist] Could not write wishlist to localStorage:', err);
  }
  document.dispatchEvent(
    new CustomEvent('wishlist:updated', {
      bubbles: true,
      cancelable: true,
      detail: { count: list.length, handle },
    })
  );
}

/**
 * @namespace VTWishlist
 * Public wishlist API. Also exported from this module.
 */
export const VTWishlist = {
  /** The localStorage key used for persistence. */
  STORAGE_KEY,

  /**
   * Adds a product handle to the wishlist (idempotent — moves to front if present).
   * @param {string} handle - The product handle (or numeric id coerced to string).
   */
  add(handle) {
    if (!handle) return;
    const h = String(handle);
    let list = readStore();
    list = list.filter((x) => x !== h);
    list.unshift(h);
    writeStore(list, h);
  },

  /**
   * Removes a product handle from the wishlist.
   * @param {string} handle - The product handle to remove.
   */
  remove(handle) {
    if (!handle) return;
    const h = String(handle);
    const list = readStore().filter((x) => x !== h);
    writeStore(list, h);
  },

  /**
   * Returns true if the handle is in the wishlist.
   * @param {string} handle - The product handle to check.
   * @returns {boolean}
   */
  has(handle) {
    if (!handle) return false;
    return readStore().includes(String(handle));
  },

  /**
   * Returns a copy of the wishlist array (handles in stored order).
   * @returns {string[]}
   */
  getAll() {
    return readStore().slice();
  },

  /**
   * Returns the number of items in the wishlist.
   * @returns {number}
   */
  getCount() {
    return readStore().length;
  },

  /** Empties the wishlist. */
  clear() {
    writeStore([], '');
  },
};

/* ========================================================================== *
 * Money formatting (lightweight, standalone)
 * Shopify `/products/{handle}.js` returns `price` in cents (minor units).
 * We attempt to read the shop's money format from the global; fall back to
 * `$X.XX`-style formatting using the document's currency hints.
 * ========================================================================== */

/**
 * Formats a price in minor units (cents) to a display string.
 * Reads `window.Shopify` money format when available; otherwise builds a
 * locale-aware string from the current lang attribute.
 * @param {number} cents - Price in minor units.
 * @returns {string} Formatted price string.
 */
function formatMoney(cents) {
  if (typeof cents !== 'number' || Number.isNaN(cents)) return '';
  const amount = (cents / 100).toFixed(2);

  // Use Shopify global format if present (e.g. "${{amount}}")
  if (typeof window !== 'undefined' && window.Shopify && window.Shopify.money_format) {
    return window.Shopify.money_format.replace(/\{\{amount\}\}/g, amount);
  }
  if (typeof window !== 'undefined' && window.Shopify && window.Shopify.currency && window.Shopify.currency.active) {
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en', {
        style: 'currency',
        currency: window.Shopify.currency.active,
      }).format(cents / 100);
    } catch (_) {
      /* fall through */
    }
  }
  return '$' + amount;
}

/* ========================================================================== *
 * SVG icons (inline — no external asset dependencies)
 * ========================================================================== */

const HEART_OUTLINE_SVG = `
<svg class="vt-wishlist-btn__icon vt-wishlist-btn__icon--outline" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false">
  <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"></path>
</svg>`;

const HEART_FILLED_SVG = `
<svg class="vt-wishlist-btn__icon vt-wishlist-btn__icon--filled" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false">
  <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"></path>
</svg>`;

const BAG_ICON = `
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false">
  <path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"></path>
  <line x1="3" y1="6" x2="21" y2="6"></line>
  <path d="M16 10a4 4 0 0 1-8 0"></path>
</svg>`;

const TRASH_ICON = `
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false">
  <polyline points="3 6 5 6 21 6"></polyline>
  <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
</svg>`;

/* ========================================================================== *
 * <vt-wishlist-button> — toggle button
 * ========================================================================== */

class VTWishlistButton extends HTMLElement {
  constructor() {
    super();
    this._handle = null;
    this._onClick = this._onClick.bind(this);
    this._onKeydown = this._onKeydown.bind(this);
    this._onUpdated = this._onUpdated.bind(this);
  }

  connectedCallback() {
    this._handle = this.getAttribute('data-handle') || this.getAttribute('data-id');
    if (!this._handle) {
      console.warn('[vt-wishlist-button] Missing data-handle / data-id attribute.');
      return;
    }

    // Render icon + accessible label only once.
    if (!this.hasChildNodes()) {
      this.innerHTML = HEART_OUTLINE_SVG + HEART_FILLED_SVG;
    }
    this.setAttribute('role', 'button');
    this.setAttribute('tabindex', '0');
    this._syncState();

    this.addEventListener('click', this._onClick);
    this.addEventListener('keydown', this._onKeydown);

    // React to external wishlist changes (e.g. from the wishlist page).
    document.addEventListener('wishlist:updated', this._onUpdated);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this._onClick);
    if (this._onUpdated) {
      document.removeEventListener('wishlist:updated', this._onUpdated);
    }
  }

  /**
   * Toggles the product in the wishlist and updates the button's state.
   * Always re-syncs from storage so the icon reflects the true state.
   * @private
   */
  _onClick() {
    if (!this._handle) return;
    if (VTWishlist.has(this._handle)) {
      VTWishlist.remove(this._handle);
    } else {
      VTWishlist.add(this._handle);
    }
    this._syncState();
  }

  /**
   * Syncs state when the wishlist changes externally.
   * Skips self-triggered updates (already synced in `_onClick`).
   * @private
   */
  _onUpdated(e) {
    if (e.detail && e.detail.handle === this._handle) return;
    this._syncState();
  }

  /** Allow keyboard activation (Enter / Space). */
  _onKeydown(e) {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      this._onClick();
    }
  }

  /**
   * Reflects the current wishlist membership in the button's ARIA + class state.
   * @private
   */
  _syncState() {
    const active = VTWishlist.has(this._handle);
    this.classList.toggle('is-active', active);
    this.setAttribute('aria-pressed', active ? 'true' : 'false');
    const label = active ? 'Remove from wishlist' : 'Add to wishlist';
    this.setAttribute('aria-label', label);
    this.title = label;
  }
}

/* ========================================================================== *
 * <vt-wishlist-page> — grid of saved products
 * ========================================================================== */

class VTWishlistPage extends HTMLElement {
  constructor() {
    super();
    this._continueShoppingUrl = this.getAttribute('data-continue-url') || '/collections/all';
    this._onUpdated = this.render.bind(this);
  }

  connectedCallback() {
    document.addEventListener('wishlist:updated', this._onUpdated);
    this.render();
  }

  disconnectedCallback() {
    document.removeEventListener('wishlist:updated', this._onUpdated);
  }

  /**
   * Re-renders the wishlist: empty state or product grid.
   * Fetches each product via `/products/{handle}.js` (parallel, deduped).
   */
  async render() {
    const handles = VTWishlist.getAll();

    if (handles.length === 0) {
      this.innerHTML = this._emptyStateMarkup();
      return;
    }

    // Show a loading skeleton immediately.
    this.innerHTML = `
      <div class="vt-wishlist-page__loading" role="status" aria-live="polite">
        <p>Loading your wishlist…</p>
      </div>`;

    const results = await Promise.allSettled(
      handles.map((h) => this._fetchProduct(h))
    );

    const products = [];
    results.forEach((r, i) => {
      if (r.status === 'fulfilled' && r.value) {
        products.push(r.value);
      } else {
        console.warn('[vt-wishlist-page] Could not load product:', handles[i]);
      }
    });

    // If all fetches failed or products were removed, show empty state.
    if (products.length === 0) {
      this.innerHTML = this._emptyStateMarkup();
      return;
    }

    this.innerHTML = `
      <div class="vt-wishlist-page__header">
        <h1 class="vt-wishlist-page__title">My Wishlist</h1>
        <span class="vt-wishlist-page__count">${products.length} item${products.length === 1 ? '' : 's'}</span>
      </div>
      <div class="vt-wishlist-page__actions">
        <button type="button" class="button vt-wishlist-page__add-all" data-vt-add-all>
          ${BAG_ICON}
          <span>Add all to cart</span>
        </button>
        <button type="button" class="button-secondary vt-wishlist-page__clear" data-vt-clear>
          <span>Clear wishlist</span>
        </button>
      </div>
      <div class="vt-wishlist-page__grid">
        ${products.map((p) => this._productCardMarkup(p)).join('')}
      </div>`;

    // Wire up remove buttons.
    this.querySelectorAll('[data-vt-remove]').forEach((btn) => {
      btn.addEventListener('click', () => {
        VTWishlist.remove(btn.getAttribute('data-vt-remove'));
        // render() will be called by the wishlist:updated listener.
      });
    });

    // Wire up "Add all to cart".
    const addAllBtn = this.querySelector('[data-vt-add-all]');
    if (addAllBtn) {
      addAllBtn.addEventListener('click', () => this._addAllToCart(products));
    }

    // Wire up "Clear wishlist".
    const clearBtn = this.querySelector('[data-vt-clear]');
    if (clearBtn) {
      clearBtn.addEventListener('click', () => {
        if (window.confirm('Remove all items from your wishlist?')) {
          VTWishlist.clear();
        }
      });
    }
  }

  /**
   * Fetches product JSON via the Shopify `/products/{handle}.js` AJAX endpoint.
   * @param {string} handle - The product handle.
   * @returns {Promise<object|null>} The product object, or null on failure.
   * @private
   */
  async _fetchProduct(handle) {
    try {
      const res = await fetch(`/products/${encodeURIComponent(handle)}.js`, {
        headers: { Accept: 'application/json' },
      });
      if (!res.ok) return null;
      return await res.json();
    } catch (err) {
      console.warn('[vt-wishlist-page] Fetch error for', handle, err);
      return null;
    }
  }

  /**
   * Builds the markup for a single product card.
   * @param {object} product - The product object from `/products/{handle}.js`.
   * @returns {string} HTML string.
   * @private
   */
  _productCardMarkup(product) {
    const handle = product.handle;
    const title = product.title || 'Untitled product';
    const url = product.url || `/products/${handle}`;
    const price = product.price != null ? formatMoney(product.price) : '';
    const compareAt = product.compare_at_price && product.compare_at_price > product.price
      ? formatMoney(product.compare_at_price)
      : '';
    const onSale = Boolean(compareAt);

    // The /products/{handle}.js response includes a `featured_image` string.
    const imageSrc = product.featured_image || '';
    const imageAlt = title;

    // First available variant id for add-to-cart form.
    const variantId = product.variants && product.variants.length
      ? (product.variants.find((v) => v.available) || product.variants[0]).id
      : null;

    const soldOut = product.available === false;

    return `
      <article class="vt-wishlist-card" data-handle="${handle}">
        <a class="vt-wishlist-card__media" href="${url}" aria-label="${this._escapeAttr(title)}">
          ${imageSrc
            ? `<img src="${this._escapeAttr(imageSrc)}" alt="${this._escapeAttr(imageAlt)}" loading="lazy" width="400" height="400" class="vt-wishlist-card__img">`
            : `<div class="vt-wishlist-card__img-placeholder" aria-hidden="true"></div>`}
          ${onSale ? '<span class="vt-wishlist-card__badge vt-wishlist-card__badge--sale">Sale</span>' : ''}
          ${soldOut ? '<span class="vt-wishlist-card__badge vt-wishlist-card__badge--soldout">Sold out</span>' : ''}
        </a>
        <div class="vt-wishlist-card__body">
          <h3 class="vt-wishlist-card__title">
            <a href="${url}">${this._escapeHtml(title)}</a>
          </h3>
          <div class="vt-wishlist-card__price">
            <span class="vt-wishlist-card__price-current">${price}</span>
            ${onSale ? `<span class="vt-wishlist-card__price-compare">${compareAt}</span>` : ''}
          </div>
          <div class="vt-wishlist-card__actions">
            <form action="/cart/add" method="post" enctype="multipart/form-data" class="vt-wishlist-card__form" data-vt-add-form>
              <input type="hidden" name="id" value="${variantId || ''}">
              <input type="hidden" name="quantity" value="1">
              <button type="submit" class="button vt-wishlist-card__add" ${soldOut || !variantId ? 'disabled' : ''}>
                ${BAG_ICON}
                <span>${soldOut ? 'Sold out' : 'Add to cart'}</span>
              </button>
            </form>
            <button type="button" class="button-secondary vt-wishlist-card__remove" data-vt-remove="${handle}" aria-label="Remove ${this._escapeAttr(title)} from wishlist">
              ${TRASH_ICON}
              <span class="visually-hidden">Remove</span>
            </button>
          </div>
        </div>
      </article>`;
  }

  /**
   * Adds all (available, in-stock) wishlist products to the cart in one request
   * using the `/cart/add.js` endpoint with an items array.
   * @param {object[]} products - Array of product objects.
   * @private
   */
  async _addAllToCart(products) {
    const items = products
      .filter((p) => p.variants && p.variants.length)
      .map((p) => {
        const v = p.variants.find((x) => x.available) || p.variants[0];
        return { id: v.id, quantity: 1 };
      })
      .filter((it) => it.id);

    if (items.length === 0) {
      window.alert('No items can be added to the cart.');
      return;
    }

    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        window.alert(data.description || data.message || 'Could not add items to cart.');
        return;
      }
      // Redirect to cart so the customer sees their additions.
      window.location.href = '/cart';
    } catch (err) {
      console.error('[vt-wishlist-page] addAllToCart failed:', err);
      window.alert('Something went wrong adding items to cart. Please try again.');
    }
  }

  /**
   * Returns the empty-state markup.
   * @returns {string} HTML string.
   * @private
   */
  _emptyStateMarkup() {
    return `
      <div class="vt-wishlist-empty">
        <div class="vt-wishlist-empty__icon" aria-hidden="true">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
            <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"></path>
          </svg>
        </div>
        <h1 class="vt-wishlist-empty__heading">Your wishlist is empty</h1>
        <p class="vt-wishlist-empty__text">Save your favourite items here for later.</p>
        <a href="${this._escapeAttr(this._continueShoppingUrl)}" class="button vt-wishlist-empty__cta">
          Continue shopping
        </a>
      </div>`;
  }

  /** Escapes a string for safe insertion into an HTML attribute. @private */
  _escapeAttr(str) {
    return String(str == null ? '' : str)
      .replace(/&/g, '&amp;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
  }

  /** Escapes a string for safe insertion into HTML text content. @private */
  _escapeHtml(str) {
    return String(str == null ? '' : str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
  }
}

/* ========================================================================== *
 * Register custom elements (guard against duplicate registration)
 * ========================================================================== */

if (!customElements.get('vt-wishlist-button')) {
  customElements.define('vt-wishlist-button', VTWishlistButton);
}
if (!customElements.get('vt-wishlist-page')) {
  customElements.define('vt-wishlist-page', VTWishlistPage);
}

/* ========================================================================== *
 * Module exports
 * ========================================================================== */

export { VTWishlistButton, VTWishlistPage };
export default VTWishlist;
